import { createLeagueApi, adminApi } from './api';
import { clearLeagueCache } from './apiCache.js';

const POLL_MS = 2000;
const STALL_MS = 3 * 60 * 1000;
const MAX_WAIT_MS = 20 * 60 * 1000;
const STAGE_PROGRESS = { queued: 10, retrying: 10, running: 40, computing: 80, building: 90 };

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** The in-progress text for an add: its stage, or reassurance once it has stalled. */
export function syncLabel({ stage, progress, stalled }) {
  if (stalled) return 'Still working…';
  return `${stage === 'computing' ? 'Calculating' : 'Syncing'}… ${progress}%`;
}

function tooLong() {
  return new Error('This league is still syncing. Check back in a few minutes.');
}

async function followJob(api, jobId, onUpdate) {
  const deadline = Date.now() + MAX_WAIT_MS;
  let seen = null;
  let changedAt = Date.now();
  while (Date.now() < deadline) {
    let job = null;
    try {
      job = await api.getSyncJob(jobId);
    } catch {
      // A failed poll is retried; only a job that stops moving is reported.
    }
    if (job?.status === 'completed') return;
    if (job?.status === 'failed') throw new Error('Sync failed. Please try again.');
    const key = job ? `${job.status}|${job.updated_at}` : seen;
    if (key !== seen) {
      seen = key;
      changedAt = Date.now();
    }
    const status = job?.status ?? 'queued';
    onUpdate({
      status: 'syncing',
      stage: status === 'computing' ? 'computing' : 'syncing',
      progress: STAGE_PROGRESS[status] ?? STAGE_PROGRESS.queued,
      stalled: Date.now() - changedAt > STALL_MS,
    });
    await sleep(POLL_MS);
  }
  throw tooLong();
}

/** Poll until the league is ready: synced and its past weeks built. `stage` is
 * what the wait reports: 'syncing' with no job to follow, 'computing' after one. */
async function waitUntilReady(leagueId, onUpdate, stage) {
  const startedAt = Date.now();
  const progress = stage === 'computing' ? STAGE_PROGRESS.building : STAGE_PROGRESS.running;
  while (Date.now() - startedAt < MAX_WAIT_MS) {
    if (await adminApi.checkLeagueReady(leagueId)) return;
    onUpdate({ status: 'syncing', stage, progress, stalled: Date.now() - startedAt > STALL_MS });
    await sleep(POLL_MS);
  }
  throw tooLong();
}

/**
 * Join a league (idempotent), follow the sync it starts, wait until the league
 * is ready (its past weeks built too), then navigate there. Every request fetches a fresh Clerk token: a
 * session token expires within about a minute and a sync can run longer.
 *
 * @param {Object} opts
 * @param {string} opts.leagueId
 * @param {() => Promise<string>} opts.getToken - Clerk getToken()
 * @param {(path: string) => void} opts.navigate - react-router navigate()
 * @param {(state: {status: 'syncing'|'error', stage?: 'syncing'|'computing', progress?: number, stalled?: boolean, message?: string}) => void} [opts.onUpdate]
 */
export async function joinLeague({ leagueId, getToken, navigate, onUpdate = () => {} }) {
  try {
    const api = createLeagueApi(leagueId, async (url, opts) =>
      fetch(url, { ...opts, headers: { ...opts?.headers, Authorization: `Bearer ${await getToken()}` } })
    );
    const joined = await api.joinLeague();
    if (!(await adminApi.checkLeagueReady(leagueId))) {
      onUpdate({ status: 'syncing', stage: 'syncing', progress: STAGE_PROGRESS.queued, stalled: false });
      if (joined?.job_id) await followJob(api, joined.job_id, onUpdate);
      await waitUntilReady(leagueId, onUpdate, joined?.job_id ? 'computing' : 'syncing');
    }
    clearLeagueCache(leagueId);
    navigate(`/league/${leagueId}/home`);
  } catch (err) {
    onUpdate({ status: 'error', message: err.message || 'Failed to load league' });
  }
}
